import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import RNGChart from './RNGChart';
import { selectFromInterleavedStripes } from '../utils/interleavedRng';

/**
 * RNGTestModal - Admin tool for testing RNG rolls and looking up historical rolls
 * Supports both deals and recipes with RNG enabled
 */
export default function RNGTestModal({ item, itemType, onClose }) {
  const [mode, setMode] = useState('test'); // 'test' or 'lookup'
  const [lookupId, setLookupId] = useState('');
  const [rollResult, setRollResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [enrichedRngPool, setEnrichedRngPool] = useState([]);
  const [collectionItems, setCollectionItems] = useState([]);
  const [rollHistory, setRollHistory] = useState([]); // Track last 10 rolls

  // Fetch collection items for enrichment
  useEffect(() => {
    const fetchCollectionItems = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin?action=dcl-collections`);
        const data = await response.json();
        setCollectionItems(data.collections || []);
      } catch (error) {
        console.error('Failed to fetch collection items:', error);
      }
    };
    fetchCollectionItems();
  }, []);

  // Extract RNG pool based on item type
  const getRngPool = () => {
    if (itemType === 'deal') {
      const rngPoolChances = item.rng_pool || [];
      const dealItems = item.deal_items || [];
      
      // For deals, rng_pool is just an array of numbers [25, 25, 25, 25]
      // We need to combine it with deal_items to create proper objects
      if (Array.isArray(rngPoolChances) && rngPoolChances.length > 0) {
        // If rng_pool items are already objects with collection info, use them
        if (typeof rngPoolChances[0] === 'object' && rngPoolChances[0] !== null) {
          return rngPoolChances;
        }
        
        // Otherwise, combine rng_pool chances with deal_items
        return rngPoolChances.map((chance, idx) => {
          const dealItem = dealItems[idx] || {};
          return {
            dcl_collection_address: dealItem.dcl_collection_address,
            dcl_item_id: dealItem.dcl_item_id,
            deal_image: dealItem.deal_image,
            dcl_price_mana: dealItem.dcl_price_mana,
            chance: chance
          };
        });
      }
      
      return [];
    } else if (itemType === 'recipe') {
      const rewards = item.rewards || [];
      // Recipe rewards have collection, item_id, chance
      return rewards;
    }
    return [];
  };

  // Enrich RNG pool when collection items are loaded
  useEffect(() => {
    if (collectionItems.length === 0) return;
    
    if (itemType === 'deal') {
      // For deals: combine rng_pool (chances) with deal_items (metadata)
      const rngPoolChances = item.rng_pool || [];
      const dealItems = item.deal_items || [];
      
      const enriched = rngPoolChances.map((chance, idx) => {
        const dealItem = dealItems[idx];
        if (!dealItem) {
          return { chance };
        }
        
        // Use dcl_collection_item_id to find the collection item by ID
        const collectionItemId = dealItem.dcl_collection_item_id;
        const collectionItem = collectionItems.find(ci => ci.id === collectionItemId);
        
        return {
          dcl_collection_address: dealItem.dcl_collection_address,
          dcl_item_id: dealItem.dcl_item_id,
          deal_image: dealItem.deal_image,
          dcl_price_mana: dealItem.dcl_price_mana,
          chance: chance,
          name: collectionItem?.title || `Item #${dealItem.dcl_item_id}`,
          item_title: collectionItem?.title || `Item #${dealItem.dcl_item_id}`,
          image: dealItem.deal_image || collectionItem?.image || ''
        };
      });
      
      setEnrichedRngPool(enriched);
      
    } else if (itemType === 'recipe') {
      // For recipes: rewards already have collection, item_id, chance
      const rawRewards = item.rewards || [];
      
      const enriched = rawRewards.map((reward, idx) => {
        if (!reward) return reward;
        
        const collection = (reward.collection || '').toLowerCase();
        const itemId = reward.item_id;
        
        if (!collection) {
          return reward;
        }
        
        // Find matching collection item - GOLDEN RULE: Match by urn_item_id
        const collectionItem = collectionItems.find(ci => {
          const ciAddr = ci.collection_address.toLowerCase();
          return ciAddr === collection && ci.urn_item_id === String(itemId);
        });
        
        if (collectionItem) {
          return {
            ...reward,
            name: reward.name || collectionItem.title,
            item_title: reward.item_title || collectionItem.title,
            collection_title: reward.collection_title || collectionItem.title,
            image: reward.image || collectionItem.image
          };
        }
        
        return reward;
      });
      
      setEnrichedRngPool(enriched);
    }
  }, [collectionItems, item, itemType]);

  const rngPool = enrichedRngPool.length > 0 ? enrichedRngPool : getRngPool();
  const totalChance = rngPool.reduce((sum, r) => sum + (Number(r.chance) || 0), 0);

  // Simulate a test roll (client-side, no logging)
  const handleTestRoll = () => {
    if (rngPool.length === 0) {
      setError('No RNG pool items configured');
      return;
    }

    // Generate random value between 0 and 1
    const randomValue = Math.random();

    // Use true interleaved stripe selection (matches backend implementation)
    const rngResult = selectFromInterleavedStripes(rngPool, randomValue, 'chance');
    const selectedItem = rngResult.selectedItem;
    const selectedIndex = rngResult.selectedIndex;

    const itemName = selectedItem?.name || selectedItem?.item_title || selectedItem?.collection_title || `Item ${selectedIndex + 1}`;
    console.log(`🎲 Rolled: ${itemName}`);
    
    // Add to roll history (keep last 10)
    setRollHistory(prev => {
      const newHistory = [{
        name: itemName,
        timestamp: new Date(),
        randomValue: randomValue
      }, ...prev];
      return newHistory.slice(0, 10); // Keep only last 10
    });

    setRollResult({
      random_value: randomValue,
      rng_pool: rngPool,
      minted_item: selectedItem,
      isTestRoll: true
    });
    setError(null);
  };

  // Lookup a historical roll from rng_audit_logs
  const handleLookup = async () => {
    if (!lookupId.trim()) {
      setError('Please enter an RNG Audit Log ID');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin?action=rng-lookup&id=${lookupId}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch RNG audit log');
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }

      // Format the data to match what RNGChart expects
      setRollResult({
        random_value: data.random_value,
        rng_pool: data.all_items,
        minted_item: data.selected_item,
        isTestRoll: false,
        timestamp: data.created_at,
        wallet: data.wallet,
        source: data.source,
        tx_hash: data.tx_hash
      });
      setError(null);
    } catch (err) {
      console.error('Lookup error:', err);
      setError(err.message || 'Failed to lookup RNG audit log');
      setRollResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setRollResult(null);
    setError(null);
    setLookupId('');
    // Don't clear roll history on reset
  };

  const handleRollAgain = () => {
    // For test rolls, immediately roll again without resetting
    if (rollResult?.isTestRoll) {
      handleTestRoll();
    } else {
      handleReset();
    }
  };

  const modalContent = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
        overflow: 'auto'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1a1a2e',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '2px solid rgba(138, 43, 226, 0.5)',
          boxShadow: '0 8px 32px rgba(138, 43, 226, 0.3)'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          borderBottom: '1px solid rgba(138, 43, 226, 0.3)',
          paddingBottom: '1rem'
        }}>
          <div>
            <h2 style={{ 
              color: '#8a2be2', 
              margin: 0,
              fontSize: '1.5rem',
              marginBottom: '0.5rem'
            }}>
              🎲 RNG Testing Tool
            </h2>
            <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
              {itemType === 'deal' ? `Deal: ${item.deal_name}` : `Recipe: ${item.title}`}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '2rem',
              cursor: 'pointer',
              padding: '0',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Mode Selector */}
        {!rollResult && (
          <div style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '2rem',
            borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
            paddingBottom: '1rem'
          }}>
            <button
              onClick={() => setMode('test')}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: mode === 'test' ? 'rgba(138, 43, 226, 0.3)' : 'rgba(138, 43, 226, 0.1)',
                border: mode === 'test' ? '2px solid #8a2be2' : '2px solid transparent',
                color: mode === 'test' ? '#fff' : '#ccc',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
            >
              🎯 Test Roll
            </button>
            <button
              onClick={() => setMode('lookup')}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: mode === 'lookup' ? 'rgba(138, 43, 226, 0.3)' : 'rgba(138, 43, 226, 0.1)',
                border: mode === 'lookup' ? '2px solid #8a2be2' : '2px solid transparent',
                color: mode === 'lookup' ? '#fff' : '#ccc',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
            >
              🔍 Success Lookup
            </button>
          </div>
        )}

        {/* Test Roll Mode */}
        {mode === 'test' && !rollResult && (
          <div>
            <div style={{
              backgroundColor: 'rgba(138, 43, 226, 0.1)',
              border: '1px solid rgba(138, 43, 226, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ color: '#8a2be2', marginTop: 0 }}>RNG Pool Preview</h4>
              <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
                <strong>Total Items:</strong> {rngPool.length}<br />
                <strong>Total Chance:</strong> {totalChance}
              </div>
              <div style={{ marginTop: '1rem' }}>
                {rngPool.map((rewardItem, idx) => {
                  const itemName = rewardItem.name || rewardItem.item_title || rewardItem.collection_title || `Item ${idx + 1}`;
                  const chance = Number(rewardItem.chance) || 0;
                  const percentage = totalChance > 0 ? ((chance / totalChance) * 100).toFixed(1) : '0.0';
                  
                  return (
                    <div key={idx} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {rewardItem.image && (
                          <img 
                            src={rewardItem.image} 
                            alt={itemName}
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              objectFit: 'cover'
                            }}
                          />
                        )}
                        <span style={{ color: '#fff' }}>{itemName}</span>
                      </div>
                      <span style={{ color: '#8a2be2', fontWeight: 'bold' }}>
                        {percentage}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#ccc', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Click below to simulate a random roll. This is for testing only and does not log to the database.
              </p>
              <button
                onClick={handleTestRoll}
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: '#8a2be2',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 4px 16px rgba(138, 43, 226, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 6px 20px rgba(138, 43, 226, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 16px rgba(138, 43, 226, 0.4)';
                }}
              >
                🎲 ROLL NOW
              </button>
            </div>
          </div>
        )}

        {/* Lookup Mode */}
        {mode === 'lookup' && !rollResult && (
          <div>
            <div style={{
              backgroundColor: 'rgba(138, 43, 226, 0.1)',
              border: '1px solid rgba(138, 43, 226, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <h4 style={{ color: '#8a2be2', marginTop: 0 }}>Lookup Historical Roll</h4>
              <p style={{ color: '#ccc', fontSize: '0.9rem', margin: '0.5rem 0 1rem 0' }}>
                Enter the <code style={{ backgroundColor: 'rgba(0,0,0,0.3)', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>rng_audit_logs.id</code> to see the exact RNG result that a user received.
              </p>
              
              <input
                type="text"
                placeholder="Enter RNG Audit Log ID (e.g., 12345)"
                value={lookupId}
                onChange={(e) => setLookupId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLookup()}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(138, 43, 226, 0.5)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  marginBottom: '1rem'
                }}
              />
              
              <button
                onClick={handleLookup}
                disabled={isLoading || !lookupId.trim()}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: isLoading || !lookupId.trim() ? 'rgba(138, 43, 226, 0.3)' : '#8a2be2',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: isLoading || !lookupId.trim() ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {isLoading ? '🔄 Looking up...' : '🔍 Lookup Roll'}
              </button>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div style={{
            backgroundColor: 'rgba(220, 38, 38, 0.2)',
            border: '1px solid rgba(220, 38, 38, 0.5)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            color: '#ef4444',
            textAlign: 'center'
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Result Display */}
        {rollResult && (
          <div>
            {/* Test Roll Banner */}
            {rollResult.isTestRoll && (
              <div style={{
                backgroundColor: 'rgba(251, 191, 36, 0.2)',
                border: '1px solid rgba(251, 191, 36, 0.5)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                textAlign: 'center',
                color: '#fbbf24'
              }}>
                ⚠️ <strong>TEST ROLL</strong> - Not logged to database
              </div>
            )}

            {/* Historical Roll Info */}
            {!rollResult.isTestRoll && (
              <div style={{
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                border: '1px solid rgba(59, 130, 246, 0.5)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                fontSize: '0.9rem',
                color: '#60a5fa'
              }}>
                <div><strong>Wallet:</strong> {rollResult.wallet}</div>
                <div><strong>Source:</strong> {rollResult.source}</div>
                {rollResult.tx_hash && <div><strong>TX Hash:</strong> {rollResult.tx_hash}</div>}
                {rollResult.timestamp && (
                  <div><strong>Timestamp:</strong> {new Date(rollResult.timestamp).toLocaleString()}</div>
                )}
              </div>
            )}

            {/* RNG Chart */}
            <RNGChart
              randomValue={rollResult.random_value}
              rewards={rollResult.rng_pool}
              selectedReward={rollResult.minted_item}
              totalChance={rollResult.rng_pool.reduce((sum, r) => sum + (Number(r.chance) || 0), 0)}
            />

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '1.5rem'
            }}>
              <button
                onClick={handleRollAgain}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'rgba(138, 43, 226, 0.2)',
                  border: '1px solid rgba(138, 43, 226, 0.5)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {rollResult.isTestRoll ? '🎲 Roll Again' : '🔍 Lookup Another'}
              </button>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.5)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ✕ Close
              </button>
            </div>
          </div>
        )}

        {/* Last 10 Rolls History */}
        {mode === 'test' && rollHistory.length > 0 && (
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid rgba(138, 43, 226, 0.3)'
          }}>
            <h4 style={{ 
              color: '#8a2be2', 
              marginTop: 0,
              marginBottom: '1rem',
              fontSize: '1rem'
            }}>
              📜 Last {rollHistory.length} Roll{rollHistory.length !== 1 ? 's' : ''}
            </h4>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '0.5rem'
            }}>
              {rollHistory.map((roll, idx) => (
                <div key={idx} style={{
                  padding: '0.5rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  border: '1px solid rgba(138, 43, 226, 0.2)',
                  fontSize: '0.85rem'
                }}>
                  <div style={{ 
                    color: '#fff',
                    fontWeight: 'bold',
                    marginBottom: '0.25rem'
                  }}>
                    {roll.name}
                  </div>
                  <div style={{ 
                    color: '#888',
                    fontSize: '0.75rem'
                  }}>
                    Roll: {roll.randomValue.toFixed(4)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

